import React, { useState, useEffect } from 'react';
import { decryptMessage } from '../../utils/encryption';

export default function ForwardModal({ msg, conversations, users = [], currentUserId, onForward, onClose }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => { setSelected([]); setSearch(''); }, [msg]);

  if (!msg) return null;

  // Merge: existing conversations + users with no direct chat yet
  const directUserIds = new Set(
    conversations.filter(c => c.type === 'direct' && c.other_user).map(c => Number(c.other_user.id))
  );
  const convItems = conversations.map(c => ({
    key: `c${c.id}`, convId: c.id, name: c.name || 'Chat',
    avatar_url: c.avatar_url, subtitle: c.type === 'group' ? 'Group' : c.type === 'self' ? 'Saved messages' : 'Direct'
  }));
  const userItems = users
    .filter(u => Number(u.id) !== Number(currentUserId) && !directUserIds.has(Number(u.id)))
    .map(u => ({
      key: `u${u.id}`, userId: u.id, name: u.full_name || 'User',
      avatar_url: u.avatar_url, subtitle: 'New chat'
    }));
  const allItems = [...convItems, ...userItems];
  const filtered = allItems.filter(it =>
    (it.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const _isMulti = Array.isArray(msg);
  const _one = _isMulti ? msg[0] : msg;
  const preview = _isMulti && msg.length > 1 ? `${msg.length} messages`
    : _one.message_type === 'image' ? '📷 Photo'
    : _one.message_type === 'file' ? `📎 ${_one.file_name}`
    : decryptMessage(_one.content)?.substring(0, 80);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-teal-500">
                <polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/>
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900 dark:text-white text-sm">Forward Message</p>
              <p className="text-xs text-gray-400">Select conversations</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Message preview */}
        <div className="mx-4 mt-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border-l-4 border-teal-400">
          <p className="text-xs text-gray-400 mb-0.5">Forwarding</p>
          <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{preview}</p>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" placeholder="Search conversations..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
          </div>
        </div>

        {/* Conversation list */}
        <div className="overflow-y-auto px-4 pb-2" style={{maxHeight:'240px'}}>
          {filtered.map(conv => {
            const isSelected = selected.includes(conv.key);
            return (
              <div key={conv.key} onClick={() => toggle(conv.key)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition mb-1
                  ${isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden">
                  {conv.avatar_url?.startsWith('https://')
                    ? <img src={conv.avatar_url?.startsWith('https://')?conv.avatar_url:`https://qtalk.qualtechedge.in${conv.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                    : (conv.name?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{conv.name || 'Chat'}</p>
                  <p className={`text-xs ${conv.subtitle === 'New chat' ? 'text-teal-500 font-medium' : 'text-gray-400'}`}>{conv.subtitle}</p>
                </div>
                {/* Checkbox */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition
                  ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300 dark:border-gray-600'}`}>
                  {isSelected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-6">No conversations found</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            {selected.length > 0 ? `${selected.length} selected` : 'Select at least one'}
          </p>
          <button
            onClick={() => {
              if (selected.length === 0) return;
              const targets = selected.map(k => allItems.find(it => it.key === k)).filter(Boolean)
                .map(it => it.convId ? { convId: it.convId } : { userId: it.userId });
              onForward(msg, targets); onClose();
            }}
            disabled={selected.length === 0}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition disabled:opacity-40 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/>
            </svg>
            Forward
          </button>
        </div>
      </div>
    </div>
  );
}
